/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from 'react';
import StyledImage from '@apps/components/Image';
import ClearIcon from '@mui/icons-material/Clear';
import { Box, Button, Card, Stack, TextField, Typography } from '@mui/material';
import Grid, { GridProps } from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@stack/assets/icons/search-primary.svg';
import { useGetResponsive } from '@stack/helpers/lib/responsive';
import { TitleDivider } from '@stack/ui/components';
import { useRouter } from 'next/router';

export type CarBrandSectionProps = GridProps & {
  onFilterBrand: ({
    slug,
    params,
    routingMethod,
  }: {
    slug?: string | undefined;
    params?: Record<string, string> | undefined;
    routingMethod?: 'push' | 'replace';
  }) => Promise<void>;

  data: CarBrand;
  webView?: boolean;
  title: string;
  containerProps?: GridProps;
};

export type CarBrand = {
  car_brand_image: string;
  car_brand_description: string;
  webView?: boolean;
};

/**
 * ===========================
 * MAIN
 * ===========================
 */
export const CarDescriptionSection: React.FC<CarBrandSectionProps> = (
  props
) => {
  const { onFilterBrand, data, title, containerProps, webView } = props;
  // ======================= HOOKS
  const router = useRouter();

  const [keyword, setKeyword] = useState<string>('');

  const { isMobile } = useGetResponsive();

  // =============== VARIABLES
  const slug = router?.query?.slug as string;
  const queryKeyword = router?.query?.keyword as string | undefined;

  // =============== EFFECTS
  useEffect(() => {
    setKeyword(queryKeyword || '');
  }, [queryKeyword]);

  // =============== EVENTS
  const onFilter = useCallback(
    (keyword?: string) => {
      onFilterBrand({
        slug,
        params: keyword ? { keyword } : undefined,
        routingMethod: 'replace',
      });
    },
    [onFilterBrand, slug]
  );

  // =============== VIEWS
  if (!data) return null;
  return (
    <TitleDivider
      titleVariant="h1"
      title={title}
      webView={webView}
      rightContent={
        <Box sx={{ marginLeft: { md: 'auto' }, pt: { xs: 0, md: 3 } }}>
          <TextField
            variant="outlined"
            fullWidth
            placeholder="BYD Seagull"
            value={keyword}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onFilter(keyword);
              }
            }}
            onChange={(val) => setKeyword(val.target.value.toString())}
            sx={{
              borderRadius: 40,
              backgroundColor: 'transparent',
              '& .MuiOutlinedInput-root': {
                fontWeight: 500,
                padding: '3px 3px 3px 14px !important',
                '.MuiOutlinedInput-input': {
                  padding: '0 !important',
                },
                '& fieldset': {
                  border: 'none !important',
                },
                '&:hover fieldset': {
                  border: 'none !important',
                },
                '&.Mui-focused fieldset': {
                  border: '1px solid #31ACBF !important',
                  borderRadius: 40,
                },
              },
              marginBottom: {
                xs: '8px',
                md: 0,
              },
            }}
            InputProps={{
              sx: {
                '& input::placeholder': {
                  fontWeight: 500,
                  color: '#A8B5C9 !important',
                },
              },
              startAdornment: (
                <Stack sx={{ alignItems: 'center', marginRight: 2 }}>
                  <SearchIcon />
                </Stack>
              ),
              endAdornment: (
                <Stack direction="row" gap={1}>
                  {!!queryKeyword && (
                    <IconButton
                      onClick={() => {
                        setKeyword('');
                        onFilter();
                      }}
                    >
                      <ClearIcon fontSize={'small'} />
                    </IconButton>
                  )}
                  <Button
                    onClick={() => {
                      onFilter(keyword);
                    }}
                    variant={'contained'}
                    sx={{
                      borderRadius: 59,
                      backgroundColor: '#30A6B9',
                      padding: '10px 26px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      lineHeight: '1rem',
                      boxShadow: 'none !important',
                    }}
                  >
                    Search
                  </Button>
                </Stack>
              ),
              style: {
                borderRadius: 40,
                border: '1px solid #E9ECF3',
              },
            }}
          />
        </Box>
      }
    >
      <Grid container {...containerProps}>
        <Card
          sx={{
            p: 5,
            width: '100%',
            bgcolor: '#F7F7F7',
            border: 0,
            borderRadius: '0px 0px 4px 4px',
            mb: 5,
          }}
        >
          <Stack
            direction={isMobile ? 'column' : 'row'}
            gap={5}
            alignItems={isMobile ? 'flex-start' : 'center'}
          >
            <Box
              sx={{
                height: '100%',
                maxHeight: '48px',
                maxWidth: '48px',
                minWidth: '48px',
              }}
            >
              <StyledImage src={data?.car_brand_image} />
            </Box>
            <Box
              sx={{
                //display: 'flex',
                lineHeight: '20px',
                fontSize: '14px',
                fontWeight: 400,
                //textWrap: 'wrap',
                overflowWrap: 'break-word',
              }}
            >
              <Typography>{data?.car_brand_description}</Typography>
            </Box>
          </Stack>
        </Card>
      </Grid>
    </TitleDivider>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default CarDescriptionSection;
